import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { logger } from "redux-logger";

const topicsSlice = createSlice({
  name: "topics",
  initialState: {
    topicPosts: [], // Stores posts related to the topic
    replies: [],
    suggestedTopics: [], // Stores suggested topics
    topicDetails: {}, // Stores topic-specific details (e.g., title, category, etc.)
    status: "idle", // Represents API request status
    error: null, // Stores any errors from API calls
    loading: false, // Indicates loading state
    topicHasAcceptedSolution: false,
    searchResults: {},
    topicMenuOpenId: undefined,
    reaction_count: null,
  },
  reducers: {
    setLoading: (state) => {
      state.status = "loading";
      state.loading = true;
      state.error = null; // Clear previous errors
    },
    setTopicMenuOpenId: (state, action) => {
      state.topicMenuOpenId = action.payload;
    },
    setTopicData: (state, action) => {
      const { topicPosts, suggestedTopics, topicDetails } = action.payload;
      state.status = "succeeded";
      state.loading = false;
      state.suggestedTopics = (suggestedTopics || []).filter(
        (topic) => !topic.closed
      );
      state.topicPosts = topicPosts || [];
      state.topicDetails = topicDetails || {};

      // state.topicHasAcceptedSolution = state.topicDetails.topic_accepted_answer;
      state.topicHasAcceptedSolution =
        state?.topicDetails?.hasOwnProperty("accepted_answer");
    },
    setRepliesData: (state, action) => {
      state.replies = action.payload;
    },
    setError: (state, action) => {
      state.status = "failed";
      state.loading = false;
      state.error = action.payload;
    },
    removePost: (state, action) => {
      state.topicPosts = state.topicPosts.map((post) =>
        post.id === action.payload
          ? { ...post, raw: "(تم حذف المنشور )" }
          : post
      );
    },
    addPost: (state, action) => {
      state.posts.unshift(action.payload); // Adds a new post to the top of the list
    },
    resetError: (state) => {
      state.error = null;
      state.status = "idle";
    },
    removeReply: (state, action) => {
      state.status = "succeeded";
      state.loading = false; // Ensure loading is false
      state.replies = state.replies.filter(
        (reply) => reply.id !== action.payload
      );
    },
    appendPostOfTopic: (state, action) => {
      state.status = "succeeded";
      state.loading = false;
      state.topicPosts.push(action.payload);
    },
    updatePost: (state, action) => {
      const { id, postId, title, raw, isTopic } = action.payload;

      if (isTopic) {
        if (state.topicDetails && state.topicDetails.id === id) {
          state.topicDetails.title = title;
          state.topicDetails.mainPost.raw = raw;
        }
      } else {
        if (Array.isArray(state.topicPosts)) {
          const index = state.topicPosts.findIndex(
            (post) => post.id === postId
          );
          if (index !== -1) {
            state.topicPosts[index].raw = raw;
          }
        } else if (state.topicPosts && state.topicPosts.id === postId) {
          state.topicPosts.raw = raw;
        }
      }
    },
    appendReplyOfTopic: (state, action) => {
      state.status = "succeeded";
      state.loading = false;
      state.replies.push(action.payload);
      if (
        state.topicDetails &&
        state.topicDetails.mainPost.reply_count !== undefined
      ) {
        state.topicDetails.mainPost.reply_count += 1;
      }
    },
    toggleBookmark: (state, action) => {
      state.status = "succeeded";
      state.loading = false;
      const { isTopic, postId, bookmarked, bookmark_id } = action.payload;

      if (isTopic) {
        // Update the main topic post
        state.topicDetails.mainPost = {
          ...state.topicDetails.mainPost,
          bookmarked,
          ...(bookmarked && { bookmark_id }),
        };
      } else {
        // Update a post in topicPosts
        state.topicPosts = state.topicPosts.map((post) => {
          if (post?.id === postId) {
            return { ...post, bookmarked, ...(bookmarked && { bookmark_id }) };
          }
          return post;
        });

        // Also update the post in replies if it exists there
        if (state.replies) {
          state.replies = state.replies.map((reply) => {
            if (reply?.id === postId) {
              return {
                ...reply,
                bookmarked,
                ...(bookmarked && { bookmark_id }),
              };
            }
            return reply;
          });
        }
      }
    },

    acceptUnAcceptTopicSolution: (state, action) => {
      state.status = "succeeded";
      state.loading = false;
      const { postId } = action.payload;

      // Toggle accepted solution state
      const hasAcceptedSolution = !state.topicHasAcceptedSolution;
      state.topicHasAcceptedSolution = hasAcceptedSolution;

      // Update topic details and posts
      state.topicDetails.topic_accepted_answer = hasAcceptedSolution;
      state.topicDetails.accepted_answer = hasAcceptedSolution ? postId : null; // Store post ID or null

      state.topicPosts = state.topicPosts.map((post) =>
        post.id === postId
          ? { ...post, accepted_answer: hasAcceptedSolution }
          : { ...post, accepted_answer: false }
      );
    },
    toggleLike: (state, action) => {
      state.status = "succeeded";
      state.loading = false;
      const {
        isTopic,
        postId,
        reactions,
        current_user_reaction,
        reaction_users_count,
      } = action.payload;

      if (isTopic) {
        state.topicDetails.mainPost = {
          ...state.topicDetails.mainPost,
          reactions,
          current_user_reaction,
          reaction_users_count,
        };
      } else {
        state.topicPosts = state.topicPosts.map((post) => {
          if (post?.id === postId) {
            return {
              ...post,
              reactions,
              current_user_reaction,
              reaction_users_count,
            };
          }
          return post;
        });
        if (state.replies) {
          state.replies = state.replies.map((reply) => {
            if (reply?.id === postId) {
              return {
                ...reply,
                reactions,
                current_user_reaction,
                reaction_users_count,
              };
            }
            return reply;
          });
        }
      }
    },
    setSearchResults: (state, action) => {
      state.status = "succeeded";
      state.loading = false;
      const { searchQuery, searchResult } = action.payload;

      state.searchResults = { searchQuery, searchResult };
    },
  },
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(logger),
});

export const {
  setLoading,
  setRepliesData,
  setTopicData,
  setError,
  addPost,
  resetError,
  appendPostOfTopic,
  toggleBookmark,
  acceptUnAcceptTopicSolution,
  toggleLike,
  setSearchResults,
  setTopicMenuOpenId,
  removePost,
  appendReplyOfTopic,
  removeReply,
  updatePost,
} = topicsSlice.actions;

// Thunk to fetch specific topic and its posts
export const fetchTopicData =
  (topicId, postNumber = 1) =>
  async (dispatch) => {
    try {
      dispatch(setLoading());

      if (!topicId) {
        dispatch(setError(["Topic ID is required"]));
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        throw new Error("API URL is not defined in environment variables.");
      }

      const response = await axios.get(
        `${apiUrl}/t/${topicId}/${postNumber}.json?track_visit=true&forceLoad=true&include_raw=true`
      );

      if (!response?.data?.post_stream?.posts) {
        throw new Error("Invalid API response structure.");
      }

      const {
        post_stream: { posts },
        suggested_topics = [],
        ...topicDetails
      } = response.data;

      if (!posts.length) {
        throw new Error("No posts found for this topic.");
      }

      const updatedSuggestedTopics = suggested_topics.slice(1); // Remove the first item
      topicDetails.mainPost = posts[0];

      dispatch(
        setTopicData({
          topicPosts: posts.slice(1),
          suggestedTopics: updatedSuggestedTopics,
          topicDetails,
        })
      );
    } catch (err) {
      console.error("Error fetching topic data:", err);

      let errorMessage = "An error occurred";
      if (err.response) {
        if (err.response.status === 404) {
          errorMessage = "Topic not found (404)";
        } else if (err.response.data?.errors) {
          errorMessage = err.response.data.errors.join(", ");
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      dispatch(setError([errorMessage]));
    }
  };

export const toggleBookmarkTopic = (post, isTopic) => async (dispatch) => {
  try {
    if (!post?.bookmarked) {
      //not bookmarked previously
      const formData = new FormData();
      formData.append("auto_delete_preference", 3);
      formData.append("bookmarkable_id", post?.id);
      formData.append("bookmarkable_type", "Post");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/bookmarks/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const { success, id } = response.data;

      if (success) {
        let postId = post?.id;
        dispatch(
          toggleBookmark({ isTopic, postId, bookmarked: true, bookmark_id: id })
        );
      }
    } else {
      let bookmark_id = post?.bookmark_id;
      let postId = post?.id;

      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/bookmarks/${bookmark_id}.json`
      );
      const { success } = response.data;
      if (success) {
        dispatch(toggleBookmark({ isTopic, postId, bookmarked: false }));
      }
    }
  } catch (err) {
    console.log(err);
    dispatch(setError(err));
  }
};

export const acceptUnAcceptSolution = (post) => async (dispatch, getState) => {
  const state = getState();
  const { topicHasAcceptedSolution } = state.topics;

  try {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/solution/${
        topicHasAcceptedSolution ? "unaccept" : "accept"
      }`,
      {
        id: post.id,
      }
    );
    if (response.data && response.data.success) {
      let postId = post.id;
      dispatch(acceptUnAcceptTopicSolution({ postId }));
    }
  } catch (error) {
    console.log(error);
  }
};

export const deleteTopic = (topicId) => async (dispatch) => {
  try {
    const userObj = localStorage.getItem("salla_discourse_user");
    const headers = {};

    if (userObj) {
      const user = JSON.parse(userObj);
      headers["Api-Username"] = user?.id;
      headers["Api-Key"] = user?.api_key;
    }
    await fetch(`${process.env.REACT_APP_API_URL}/t/${topicId}`, {
      method: "DELETE",
      headers,
    });
  } catch (error) {
    console.log(error);
  }
};

export const deletePost = (postId) => async (dispatch) => {
  try {
    const userObj = localStorage.getItem("salla_discourse_user");
    const headers = {};

    if (userObj) {
      const user = JSON.parse(userObj);
      headers["Api-Username"] = user?.id;
      headers["Api-Key"] = user?.api_key;
    }
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/posts/${postId}`,
      {
        method: "DELETE",
        headers,
      }
    );

    if (response.ok) {
      dispatch(removePost(postId));
    }
  } catch (error) {
    console.log(error);
  }
};

export default topicsSlice.reducer;
